/**
 * Web API adapter — browser-compatible ElectronAPI implementation.
 *
 * Reuses the same WsRpcClient + buildClientApi() + CHANNEL_MAP from the Electron app.
 * Overrides LOCAL_ONLY methods (window management, native dialogs, etc.) with web equivalents.
 *
 * Auth: browser clients use the publishing-service access token for the
 * WebSocket handshake. OK Work account state is reused from the desktop app.
 */

import { WsRpcClient } from '../../../electron/src/transport/client'
import { buildClientApi } from '../../../electron/src/transport/build-api'
import { CHANNEL_MAP } from '../../../electron/src/transport/channel-map'
import type { ElectronAPI, TransportConnectionState, WebAccessMode } from '../../../electron/src/shared/types'
import { RPC_CHANNELS, type UpdateInfo } from '@okclawpro/shared/protocol'
import { openExternalUrl } from '@okclawpro/ui'
import { getLocale, setLocale as setAppLocale, type Locale } from '@okclawpro/i18n'
import {
  preparePublicAccessMessageAttachments,
  stripStoredAttachmentPayloads,
} from './public-access-attachments'
import { downloadServerFileToBrowser } from './server-file-download'

/** Sentinel serverUrl used by the public-access (WebRTC DataChannel) path. */
const PUBLIC_ACCESS_SERVER_URL = 'datachannel://public-access'
const PUBLIC_ACCESS_SEND_MESSAGE_TIMEOUT_MS = 10 * 60_000

// ---------------------------------------------------------------------------
// Web file picker (replaces native Electron dialog)
// ---------------------------------------------------------------------------

function webFilePicker(): Promise<string[]> {
  return Promise.resolve([])
}

function downloadBrowserUrl(href: string, name = 'download'): void {
  const anchor = document.createElement('a')
  anchor.href = href
  anchor.download = name
  anchor.rel = 'noopener'
  anchor.click()
}

export function downloadBase64AssetInBrowser(
  dataBase64: string,
  mimeType: string,
  name: string,
): void {
  const binary = atob(dataBase64)
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index)
  }
  const objectUrl = URL.createObjectURL(new Blob([bytes], { type: mimeType }))
  const anchor = document.createElement('a')
  let downloadStarted = false
  try {
    anchor.href = objectUrl
    anchor.download = name
    anchor.rel = 'noopener'
    document.body.appendChild(anchor)
    anchor.click()
    downloadStarted = true
  }
  finally {
    anchor.remove()
    if (downloadStarted) {
      setTimeout(() => URL.revokeObjectURL(objectUrl), 0)
    }
    else {
      URL.revokeObjectURL(objectUrl)
    }
  }
}

// ---------------------------------------------------------------------------
// System theme detection
// ---------------------------------------------------------------------------

const darkMediaQuery = typeof window !== 'undefined'
  ? window.matchMedia('(prefers-color-scheme: dark)')
  : null

function getSystemTheme(): boolean {
  return darkMediaQuery?.matches ?? false
}

function isSupportedLocale(value: string): value is Locale {
  return value === 'en' || value === 'zh-CN'
}

function createUnsupportedUpdateInfo(currentVersion = 'unknown'): UpdateInfo {
  return {
    available: false,
    currentVersion,
    latestVersion: null,
    downloadState: 'idle',
    downloadProgress: 0,
  }
}

// ---------------------------------------------------------------------------
// Create web API
// ---------------------------------------------------------------------------

export interface WebApiOptions {
  /** WebSocket server URL (ws:// or wss://) */
  serverUrl: string
  /** Workspace ID to connect as. */
  workspaceId?: string
  /** Publishing-service access token sent during the RPC handshake. */
  accessToken?: string
  /** Logical client kind sent during the RPC handshake. */
  clientKind?: 'desktop' | 'web' | 'remote-viewer'
  /** Optional socket factory for peer-to-peer public access transports. */
  createSocket?: (url: string) => WebSocket
  reconnectStorageKey?: string
  /** Logs out only the current browser session instead of the connected device. */
  logoutCurrentClient?: () => Promise<void>
}

interface StoredReconnectIdentity {
  clientId: string
  lastSeq: number
}

function getSessionStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null
  } catch {
    return null
  }
}

function readReconnectIdentity(key?: string): StoredReconnectIdentity | undefined {
  if (!key) return undefined
  const storage = getSessionStorage()
  if (!storage) return undefined

  try {
    const raw = storage.getItem(key)
    if (!raw) return undefined
    const parsed = JSON.parse(raw) as Partial<StoredReconnectIdentity>
    if (
      typeof parsed.clientId === 'string' &&
      parsed.clientId.length > 0 &&
      typeof parsed.lastSeq === 'number' &&
      parsed.lastSeq >= 0
    ) {
      return { clientId: parsed.clientId, lastSeq: parsed.lastSeq }
    }
  } catch {
    storage.removeItem(key)
  }

  return undefined
}

function writeReconnectIdentity(key: string | undefined, identity: StoredReconnectIdentity | null): void {
  if (!key) return
  const storage = getSessionStorage()
  if (!storage) return

  try {
    if (identity) {
      storage.setItem(key, JSON.stringify(identity))
    } else {
      storage.removeItem(key)
    }
  } catch {
    // Storage can be disabled in private modes; reconnect persistence is best-effort.
  }
}

export function createWebApi(options: WebApiOptions): {
  api: ElectronAPI
  client: WsRpcClient
} {
  const { serverUrl, workspaceId, accessToken, clientKind, createSocket, reconnectStorageKey, logoutCurrentClient } = options
  const isPublicAccess = serverUrl === PUBLIC_ACCESS_SERVER_URL
  const webAccessMode: WebAccessMode = clientKind === 'remote-viewer'
    ? 'remote-viewer'
    : isPublicAccess
      ? 'public-access'
      : 'lan'

  const client = new WsRpcClient(serverUrl, {
    workspaceId,
    token: accessToken,
    clientKind,
    createSocket,
    autoReconnect: true,
    mode: 'remote',
    reconnectIdentity: readReconnectIdentity(reconnectStorageKey),
  })

  const persistReconnectIdentity = () => {
    writeReconnectIdentity(reconnectStorageKey, client.getReconnectIdentity())
  }

  client.onConnectionStateChanged((state) => {
    if (state.status === 'connected') persistReconnectIdentity()
  })
  client.onAnyEvent(() => {
    persistReconnectIdentity()
  })

  // Public access submits messages through an extra browser -> DataChannel ->
  // headless bridge -> local RPC hop. Keep LAN/desktop on the shared default,
  // but give this submit RPC more time before surfacing a false send failure.
  const channelMap = isPublicAccess
    ? {
        ...CHANNEL_MAP,
        sendMessage: {
          ...CHANNEL_MAP.sendMessage,
          timeoutMs: PUBLIC_ACCESS_SEND_MESSAGE_TIMEOUT_MS,
        },
      }
    : CHANNEL_MAP

  // Build the API proxy from the same channel map the Electron app uses
  const baseApi = buildClientApi(
    client,
    channelMap,
    (ch) => client.isChannelAvailable(ch),
  )

  const hasUpdateChannel = (channel: string) => client.isChannelAvailable(channel)
  const createUpdateUnsupportedError = () => new Error('当前服务器版本不支持检查更新，请先使用一键安装脚本升级 Linux/Docker 服务端。')

  // Override LOCAL_ONLY methods with web-compatible implementations
  const webOverrides: Partial<ElectronAPI> = {
    // Public access tunnels RPC through a WebRTC DataChannel; trim redundant
    // attachment payloads so sendMessage stays lean over the wire. LAN/desktop
    // sends are untouched (baseApi.sendMessage used as-is below).
    ...(isPublicAccess
      ? {
          sendMessage: (
            sessionId: string,
            message: string,
            attachments?: Parameters<ElectronAPI['sendMessage']>[2],
            storedAttachments?: Parameters<ElectronAPI['sendMessage']>[3],
            sendOptions?: Parameters<ElectronAPI['sendMessage']>[4],
          ) =>
            baseApi.sendMessage(
              sessionId,
              message,
              preparePublicAccessMessageAttachments(attachments),
              stripStoredAttachmentPayloads(storedAttachments),
              sendOptions,
            ),
        }
      : {}),

    // Shell operations — use browser APIs
    openUrl: (url: string) => {
      openExternalUrl(url)
      return Promise.resolve()
    },
    openFile: (path: string) => downloadServerFileToBrowser(baseApi, path),
    showInFolder: () => Promise.resolve(), // no-op in browser
    downloadAsset: async (request) => {
      if (request.sourcePath && !request.dataUrl && !request.sourceUrl) {
        await downloadServerFileToBrowser(baseApi, request.sourcePath)
        return { path: request.sourcePath }
      }
      const suggestedName = request.suggestedName ?? 'download'
      const base64Match = request.dataUrl?.match(/^data:([^;,]+);base64,([\s\S]+)$/)
      if (base64Match) {
        downloadBase64AssetInBrowser(base64Match[2], base64Match[1], suggestedName)
        return { path: suggestedName }
      }
      const href = request.dataUrl ?? request.sourceUrl
      if (!href) {
        throw new Error('No downloadable browser URL is available')
      }
      downloadBrowserUrl(href, suggestedName)
      return { path: request.suggestedName ?? request.sourceUrl ?? request.dataUrl ?? 'download' }
    },

    // File dialogs
    openFileDialog: webFilePicker,
    openFolderDialog: () => Promise.resolve(null), // not possible in browser

    // System info
    getVersions: () => ({ node: 'n/a', chrome: navigator.userAgent, electron: 'web' }),
    getRuntimeEnvironment: () => 'web',
    getWebAccessMode: () => webAccessMode,
    getSystemWarnings: () => Promise.resolve({ vcredistMissing: false }),
    isDebugMode: () => Promise.resolve(import.meta.env.DEV),
    getWindowBackgroundMaterial: () => Promise.resolve(null),

    // Locale
    getLocale: () => Promise.resolve(getLocale()),
    setLocale: async (locale: string) => {
      if (!isSupportedLocale(locale)) {
        throw new Error(`Unsupported locale: `)
      }
      await setAppLocale(locale)
    },

    // Theme
    getSystemTheme: () => Promise.resolve(getSystemTheme()),
    onSystemThemeChange: (cb: (isDark: boolean) => void) => {
      if (!darkMediaQuery) return () => {}
      const handler = (e: MediaQueryListEvent) => cb(e.matches)
      darkMediaQuery.addEventListener('change', handler)
      return () => darkMediaQuery.removeEventListener('change', handler)
    },

    // Window management — no-ops or browser equivalents
    setTrafficLightsVisible: () => Promise.resolve(),
    closeWindow: () => Promise.resolve(),
    confirmCloseWindow: () => Promise.resolve(),
    cancelCloseWindow: () => Promise.resolve(),
    onCloseRequested: () => () => {},
    getWindowFocusState: () => Promise.resolve(document.hasFocus()),
    onWindowFocusChange: (cb: (focused: boolean) => void) => {
      const onFocus = () => cb(true)
      const onBlur = () => cb(false)
      window.addEventListener('focus', onFocus)
      window.addEventListener('blur', onBlur)
      return () => {
        window.removeEventListener('focus', onFocus)
        window.removeEventListener('blur', onBlur)
      }
    },

    // Workspace operations — web UI works with a single connection
    getWindowWorkspace: () => Promise.resolve(workspaceId ?? null),
    getWindowMode: () => Promise.resolve('main'),
    // switchWorkspace must call the server so it registers the client's
    // workspaceId — otherwise push events (session updates) won't arrive.
    switchWorkspace: async (wsId: string) => {
      await client.invoke('window:switchWorkspace', wsId)
    },
    openWorkspace: async () => {},
    openSessionInNewWindow: async (_wsId: string, sessionId: string) => {
      // Open in new tab
      window.open(`${window.location.origin}/?session=${sessionId}`, '_blank')
    },

    // Auto-update: use the headless server update RPC when available.
    checkForUpdates: () => {
      if (hasUpdateChannel(RPC_CHANNELS.update.CHECK)) return baseApi.checkForUpdates()
      return Promise.reject(createUpdateUnsupportedError())
    },
    getUpdateInfo: () => {
      if (hasUpdateChannel(RPC_CHANNELS.update.GET_INFO)) return baseApi.getUpdateInfo()
      return Promise.resolve(createUnsupportedUpdateInfo(client.getServerVersion?.() ?? undefined))
    },
    downloadUpdate: () => {
      if (hasUpdateChannel(RPC_CHANNELS.update.DOWNLOAD)) return baseApi.downloadUpdate()
      return Promise.reject(createUpdateUnsupportedError())
    },
    installUpdate: () => {
      if (hasUpdateChannel(RPC_CHANNELS.update.INSTALL)) return baseApi.installUpdate()
      return Promise.reject(createUpdateUnsupportedError())
    },
    dismissUpdate: (version: string) => {
      if (hasUpdateChannel(RPC_CHANNELS.update.DISMISS)) return baseApi.dismissUpdate(version)
      return Promise.resolve()
    },
    getDismissedUpdateVersion: () => {
      if (hasUpdateChannel(RPC_CHANNELS.update.GET_DISMISSED)) return baseApi.getDismissedUpdateVersion()
      return Promise.resolve(null)
    },
    onUpdateAvailable: (cb) => baseApi.onUpdateAvailable(cb),
    onUpdateDownloadProgress: (cb) => baseApi.onUpdateDownloadProgress(cb),
    getReleaseNotes: () => Promise.resolve(''),
    getLatestReleaseVersion: () => Promise.resolve(undefined),

    // Menu events — register as keyboard shortcuts
    onMenuNewChat: () => () => {},
    onMenuOpenSettings: () => () => {},
    onMenuKeyboardShortcuts: () => () => {},
    onMenuToggleFocusMode: () => () => {},
    onMenuToggleSidebar: () => () => {},
    onDeepLinkNavigate: () => () => {},

    // Menu actions — no-ops (web has no native menu)
    menuQuit: () => Promise.resolve(),
    menuNewWindow: () => { window.open(window.location.href, '_blank'); return Promise.resolve() },
    menuMinimize: () => Promise.resolve(),
    menuMaximize: () => Promise.resolve(),
    menuZoomIn: () => Promise.resolve(),
    menuZoomOut: () => Promise.resolve(),
    menuZoomReset: () => Promise.resolve(),
    menuToggleDevTools: () => Promise.resolve(),
    menuUndo: () => { document.execCommand('undo'); return Promise.resolve() },
    menuRedo: () => { document.execCommand('redo'); return Promise.resolve() },
    menuCut: () => { document.execCommand('cut'); return Promise.resolve() },
    menuCopy: () => { document.execCommand('copy'); return Promise.resolve() },
    menuPaste: () => { document.execCommand('paste'); return Promise.resolve() },
    menuSelectAll: () => { document.execCommand('selectAll'); return Promise.resolve() },

    // Badge — use document title
    refreshBadge: () => Promise.resolve(),
    setDockIconWithBadge: () => Promise.resolve(),
    onBadgeDraw: () => () => {},
    onBadgeDrawWindows: () => () => {},

    // Notifications — Web Notifications API
    showNotification: async (title: string, body: string) => {
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(title, { body })
      }
    },
    onNotificationNavigate: () => () => {},

    // Git bash (Windows-only) — not applicable
    checkGitBash: () => Promise.resolve({ available: true } as any),
    browseForGitBash: () => Promise.resolve(null),
    setGitBashPath: () => Promise.resolve({ success: true }),
    installGitBash: () => Promise.resolve({ success: false, error: 'Git Bash install is not available in web mode' }),

    // Skills — open in browser not possible
    openSkillInEditor: () => Promise.resolve(),
    openSkillInFinder: () => Promise.resolve(),

    // Confirmation dialogs — use browser confirm()
    showLogoutConfirmation: () => Promise.resolve(window.confirm('Are you sure you want to log out?')),
    showDeleteSessionConfirmation: (name: string) => Promise.resolve(window.confirm(`Delete session "${name}"?`)),

    // Power settings — not applicable
    okclawAuthLogout: async () => {
      writeReconnectIdentity(reconnectStorageKey, null)
      if (clientKind === 'remote-viewer' && logoutCurrentClient) {
        await logoutCurrentClient()
        return
      }
      await baseApi.okclawAuthLogout()
    },
    okclawAuthRejectNewSession: async (revocationId, rejectOptions) => {
      writeReconnectIdentity(reconnectStorageKey, null)
      return baseApi.okclawAuthRejectNewSession(revocationId, rejectOptions)
    },
    onOkclawAuthStateChanged: (cb) => {
      return baseApi.onOkclawAuthStateChanged((state) => {
        if (!state.isAuthenticated) writeReconnectIdentity(reconnectStorageKey, null)
        cb(state)
      })
    },
    onOkclawAuthSessionRevoked: (cb) => {
      return baseApi.onOkclawAuthSessionRevoked((event) => {
        writeReconnectIdentity(reconnectStorageKey, null)
        cb(event)
      })
    },

    getKeepAwakeWhileRunning: () => Promise.resolve(false),
    setKeepAwakeWhileRunning: () => Promise.resolve(),

    // Transport state
    getTransportConnectionState: () => Promise.resolve(client.getConnectionState() as TransportConnectionState),
    onTransportConnectionStateChanged: (cb: (state: TransportConnectionState) => void) => {
      return client.onConnectionStateChanged(cb as any)
    },
    reconnectTransport: () => { client.reconnectNow(); return Promise.resolve() },
    isChannelAvailable: (ch: string) => client.isChannelAvailable(ch),

    // Relaunch — reload page
    relaunchApp: () => { window.location.reload(); return Promise.resolve() },
    removeWorkspace: () => Promise.resolve(false), // not supported in web UI
    invokeOnServer: () => Promise.reject(new Error('Cross-server RPC not available in web UI')),
  }

  const api = { ...baseApi, ...webOverrides } as ElectronAPI

  return { api, client }
}
